import $ from 'jquery';
import type { MaybeRefOrGetter, Ref, ShallowRef } from 'vue';
import { computed, nextTick, onMounted, reactive, ref, toRef, toValue, unref, watch } from 'vue';

import { noShowError } from '#public_ts/commons/fe-commons';
import type { ValidateFormApi } from '#public_ts/components';

import { useDisplayWidths } from './display-widths';

export type UniversalMobileDesktopPanelParentProps = {
    submit?: number;
    reset?: number;
};

export type UniversalMobileDesktopPanelProps<T> = UniversalMobileDesktopPanelParentProps & {
    initialValue?: T;
    // to switch between mobile and desktop version
    value?: T;
    store?: number;
};

export type UniversalMobileDesktopPanelParentEmits<T> = {
    (event: 'submit', value: T): void;
};

export type UniversalMobileDesktopPanelEmits<T> = UniversalMobileDesktopPanelParentEmits<T> & {
    (event: 'reject', error: Error): void;
    // to switch between mobile and desktop version
    (event: 'store', value: T): void;
};

export function useUniversalMobileDesktopPanel<T>(
    modelValue: Ref<T>,
    props: UniversalMobileDesktopPanelProps<T>,
    emit: UniversalMobileDesktopPanelEmits<T>,
    { formApi, initForm, saveForm, submitForm }: {
        formApi?: ShallowRef<ValidateFormApi | undefined>;
        initForm?: (val: T | undefined) => T | Promise<T>;
        saveForm?: (val: T) => T | Promise<T>;
        submitForm?: (val: T) => T | Promise<T>;
    } = {},
) {
    watch(() => props.submit, () => {
        const _formApi = unref(formApi);
        if(_formApi && !_formApi.validateForm()) {
            emit('reject', noShowError());
            return;
        }

        const _val = unref(modelValue);
        Promise.resolve()
            .then(() => saveForm?.(_val))
            .then((val0) => submitForm?.(val0 ?? _val))
            .then((val0) => {
                emit('submit', val0 ?? _val);
            })
            .catch((e: Error) => {
                emit('reject', e);
            });
    });

    watch(() => props.store, () => {
        const _val = unref(modelValue);
        Promise.resolve()
            .then(() => saveForm?.(_val))
            .then((val0) => {
                emit('store', val0 ?? _val);
            });
    });

    function _initForm(val: T | undefined) {
        Promise.resolve()
            .then(() => initForm?.(val))
            .then((val0) => {
                modelValue.value = (val0 ?? val ?? {}) as T;
            });
    }

    watch(() => props.reset, () => {
        nextTick(() => _initForm(props.initialValue));
    });

    watch(() => props.value, (val) => {
        if(val === unref(modelValue)) return;
        _initForm(val);
    });

    onMounted(() => {
        $(() => {
            _initForm(props.value);
        });
    });
}

export function useUniversalMobileDesktopParent<T>(
    initialValue: MaybeRefOrGetter<T | undefined>,
    props?: UniversalMobileDesktopPanelParentProps | undefined,
    emit?: UniversalMobileDesktopPanelParentEmits<T> | undefined,
    { submitForm, disableMobile, disableDesktop, devel, isMobileFormsLayoutFattappOnly }: {
        submitForm?: (val: T | undefined) => T | Promise<T>;
        disableMobile?: boolean;
        disableDesktop?: boolean;
        devel?: boolean;
        isMobileFormsLayoutFattappOnly?: boolean;
    } = {},
) {
    const initialModelValue = ref<T | undefined>(toValue(initialValue));
    const modelValue = ref<T | undefined>(unref(initialModelValue));

    const submitValue = ref(props?.submit ?? 0);
    const resetValue = ref(props?.reset ?? 0);
    const storeValue = ref(0);
    const DISPLAY_WIDTHS = useDisplayWidths();

    const requestMobileFormsLayout = computed(() => {
        if(disableMobile) return false;
        if(disableDesktop) return true;
        return isMobileFormsLayoutFattappOnly ? DISPLAY_WIDTHS.isMobileFormsLayoutFattapp : DISPLAY_WIDTHS.isMobileFormsLayout;
    });
    const isMobileFormsLayout = ref(unref(requestMobileFormsLayout));
    const isMobileView = toRef(DISPLAY_WIDTHS, 'isMobileView');

    let waitingSubmit: ((val: T) => void) | undefined;
    let waitingReject: ((error: Error) => void) | undefined;

    function submit(_value: T) {
        Promise.resolve()
            .then(() => submitForm?.(_value))
            .then((val0) => {
                const _val = val0 ?? _value;
                initialModelValue.value = _val;
                modelValue.value = _val;
                if(waitingSubmit) {
                    const _waitingSubmit = waitingSubmit;
                    waitingSubmit = undefined;
                    _waitingSubmit(_val);
                }
                emit?.('submit', _val);
            });
    }

    function reject(error: Error) {
        if(waitingReject) {
            const _waitingReject = waitingReject;
            waitingReject = undefined;
            _waitingReject(error);
        }
    }

    function resotreCurrentValue(currentValue: T) {
        // to switch between mobile and desktop version
        modelValue.value = currentValue;
        isMobileFormsLayout.value = unref(requestMobileFormsLayout);
    }

    watch(requestMobileFormsLayout, () => {
        if(devel) {
            // switch without saving
            isMobileFormsLayout.value = unref(requestMobileFormsLayout);
        } else {
            storeValue.value++;
        }
    });

    watch(() => toValue(initialValue), (val) => {
        initialModelValue.value = val;
        modelValue.value = unref(initialModelValue);
    });

    if(props) {
        watch(() => props.submit, (val) => { submitValue.value = val ?? 0; });
        watch(() => props.reset, (val) => { resetValue.value = val ?? 0; });
    }

    const bindings = reactive({
        initialValue: initialModelValue,
        value: modelValue,
        submit: submitValue,
        reset: resetValue,
        store: storeValue,
    });

    const listeners = {
        submit,
        reject,
        store: resotreCurrentValue,
    };

    function performSubmit() {
        return new Promise<T>((_resolve, _reject) => {
            waitingSubmit = _resolve;
            waitingReject = _reject;
            submitValue.value++;
        });
    }

    return {
        isMobileFormsLayout,
        isMobileView,
        bindings,
        listeners,
        performSubmit,
    };
}
